import React, { useState } from "react";
import { CircularProgress, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { parse, format } from "date-fns";
import * as selectors from "selectors";
import { useClientAvailability, useUpdateMakeupPreferences } from "../ClientAvailability/hooks";
import { useGlobalToast } from "components/GlobalToastProvider";
import SectionWrapper from "../../Widgets/SectionWrapper";

const WEEK_DAYS: Record<string, string> = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

/**
 * Generates a list of time options for the make-up session.
 */
const generateTimeOptions = (): Record<string, string> => {
  const times: Record<string, string> = {};

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = parse(`${hour}:${minute}:00`, "HH:mm:ss", new Date());
      times[`${format(time, "HH:mm:ss")}`] = format(time, "h:mm a");
    }
  }
  return times;
};

const TIME_OPTIONS = generateTimeOptions();

interface MakeupPreferences {
  day: string | null;
  time: string | null;
}

interface Service {
  makeUpDay: string | null;
  makeUpTime: string | null;
}

interface AvailabilityData {
  services: Service[];
}

interface AvailabilityResponse {
  data: AvailabilityData;
}

/**
 * Standalone MakeupSessionPreferences component that manages its own state and data fetching.
 * This component handles the make-up session preferences section independently with its own edit mode.
 * Extracts clientId from URL parameters - no props required.
 */
const MakeupSessionPreferences: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const { showToast } = useGlobalToast();
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions = useSelector(selectors.getUserPermissionsList);
  const canEdit = !isCaregiver && Boolean(userPermissions?.edit_client_availability);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [, setIsSaving] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [makeupPreferences, setMakeupPreferences] = useState<MakeupPreferences>({
    day: null,
    time: null,
  });
  const [originalPreferences, setOriginalPreferences] = useState<MakeupPreferences | null>(null);

  const {
    data: availabilityData,
    isLoading,
    refetch: refetchClientAvailability,
  } = useClientAvailability({
    clientId,
    enabled: !!clientId,
    onSuccess: ({ data }: AvailabilityResponse) => {
      if (data?.services?.[0]) {
        const service = data.services[0];
        const prefs = {
          day: service.makeUpDay || null,
          time: service.makeUpTime || null,
        };
        setMakeupPreferences(prefs);
        if (!editMode) {
          setOriginalPreferences(prefs);
        }
      }
    },
  });

  const { saveMakeupPreference } = useUpdateMakeupPreferences({
    onSuccess: () => {
      setIsSaving(false);
      setHasChanges(false);
      setEditMode(false);
      refetchClientAvailability();
      showToast({
        message: "Make-up session preferences updated successfully!",
        errorState: false,
      });
    },
    onError: () => {
      setIsSaving(false);
      showToast({
        message: "Whoops! Something went wrong",
        errorState: true,
      });
    },
  });

  const currentServices = availabilityData?.services?.[0];
  const makeUpDay = currentServices?.makeUpDay;
  const makeUpTime = currentServices?.makeUpTime;

  const handleDayChange = (e: { target: { value: string } }): void => {
    setMakeupPreferences({
      day: e.target.value || null,
      time: makeupPreferences.time ?? makeUpTime ?? null,
    });
    setHasChanges(true);
  };

  const handleTimeChange = (e: { target: { value: string } }): void => {
    setMakeupPreferences({
      day: makeupPreferences.day ?? makeUpDay ?? null,
      time: e.target.value || null,
    });
    setHasChanges(true);
  };

  const handleEdit = (): void => {
    setEditMode(true);
    setOriginalPreferences(makeupPreferences);
  };

  const handleCancel = (): void => {
    if (originalPreferences) {
      setMakeupPreferences(originalPreferences);
    }
    setEditMode(false);
    setHasChanges(false);
  };

  const handleSave = (): void => {
    if (!clientId) {
      showToast({
        message: "Client ID is missing",
        errorState: true,
      });
      return;
    }

    setIsSaving(true);
    saveMakeupPreference({
      clientId,
      makeupPreferences,
    });
  };

  const renderContent = () => {
    // If no clientId, show error
    if (!clientId) {
      return (
        <Typography variant="h6" color="error">
          Client ID is required
        </Typography>
      );
    }

    if (isLoading) {
      return <CircularProgress size={30} color="primary" style={{ marginBottom: 10 }} />;
    }

    return editMode ? (
      <Grid container spacing={7}>
        <Grid item xs={6}>
          <TextField
            select
            label="Make-up Day"
            value={makeupPreferences.day ?? makeUpDay ?? ""}
            onChange={handleDayChange}
            fullWidth
          >
            <MenuItem value="">Select Day</MenuItem>
            {Object.entries(WEEK_DAYS).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            select
            label="Make-up Time"
            value={makeupPreferences.time ?? makeUpTime ?? ""}
            onChange={handleTimeChange}
            fullWidth
          >
            <MenuItem value="">Select Time</MenuItem>
            {Object.entries(TIME_OPTIONS).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    ) : (
      <Stack gap={"8px"}>
        <Typography variant="inputLabel">Make-up Session</Typography>
        {makeUpDay && makeUpTime ? (
          <Stack spacing={2} direction={"row"} gap={"8px"}>
            <Typography variant="body2">{WEEK_DAYS[makeUpDay]}</Typography>
            <Typography variant="body2">{TIME_OPTIONS[makeUpTime]}</Typography>
          </Stack>
        ) : (
          <Typography variant="body2">No make-up session scheduled</Typography>
        )}
      </Stack>
    );
  };

  return (
    <SectionWrapper
      title="Make-up Session Preferences"
      description="Configure your client's preferred day and time for scheduling make-up sessions in case of missed appointments."
      editMode={editMode}
      onEdit={canEdit ? handleEdit : undefined}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={!hasChanges}
    >
      {renderContent()}
    </SectionWrapper>
  );
};

export default MakeupSessionPreferences;
export type { MakeupPreferences };
